import React, { FC, useEffect, useMemo, useState } from 'react';

import {
	BlueInfoIcon,
	EvCheckbox,
	EvIcon,
	EvRadioGroup,
	EvSection,
	EvSelectNew,
	EvSwitch,
	EvTitle,
	OptionType
} from '@evinced-private/ui-common';

import { SchedulingSection } from 'src/components/scheduling2';
import { SiteScannerUiToggles } from 'src/services/LocalTogglesService';

import {
	getInitialSelectedTimezone,
	getTimeValue,
	MONTHS_DAY_OPTIONS,
	parseTimeStr,
	timeOptions,
	timezoneOptions,
	WEEKDAY_OPTIONS
} from '../../../../helpers/SchedulingHelper';
import { IProperty } from '../../../../interfaces/Property';
import { IPropertyError } from '../../../../interfaces/PropertyError';
import {
	FrequencyDto,
	IScheduledJobConfiguration,
	JobTypeDto
} from '../../../../interfaces/ScheduledJobConfiguration';
import { SetPropertyType } from '../../../../pages/property-settings/PropertySettingsPageTypes';
import { useConfiguration } from '../../../../providers/configurationProvider/ConfigurationProvider';
import { URL_SET_SOURCES } from '../../../../types/UrlSetSources';
import { getRoundedUpTime } from '../../../scheduling2/helpers';

import './SchedulingSettings.scss';

const SCHEDULING_SETTINGS_TITLE = 'scheduling-settings-title';
const SCHEDULING_SETTINGS_DESCRIPTION = 'scheduling-settings-description';

interface ISchedulingSettingsProps {
	property: IProperty;
	propertyError?: IPropertyError;
	setProperty: SetPropertyType;
}

const monthlySelectionWarning =
	'If there are less days in a month, the scan will run on the last day of the month.';

export const SchedulingSettings: FC<ISchedulingSettingsProps> = ({
	property,
	propertyError,
	setProperty
}: ISchedulingSettingsProps) => {
	const initialConfig = property.scheduledJobConfiguration;

	/** State */
	const [schedulingEnabled, setSchedulingEnabled] = useState<boolean>(
		initialConfig?.active || false
	);
	const [includeCrawl, setIncludeCrawl] = useState(
		initialConfig?.jobType !== JobTypeDto.SCAN &&
			// set to false if property is in list mode
			property?.urlSetConfiguration?.source === URL_SET_SOURCES.URL_DISCOVERY
	);
	const [selectedFrequency, setSelectedFrequency] = useState<FrequencyDto>(
		initialConfig?.frequency || FrequencyDto.WEEKLY
	);
	const [selectedWeekDay, setSelectedWeekDay] = useState<OptionType>(
		WEEKDAY_OPTIONS.find((d) => d?.value === initialConfig?.wdays?.[0]) || WEEKDAY_OPTIONS[0]
	);

	const initialTime = initialConfig?.hours
		? getTimeValue(initialConfig?.hours[0], initialConfig?.minutes[0])
		: null;

	const [selectedTime, setSelectedTime] = useState<OptionType>(
		timeOptions.find((t) => t?.value === initialTime) ||
			timeOptions.find((t) => t?.value === getRoundedUpTime()?.value) ||
			timeOptions?.[0]
	);

	const [selectedTimezone, setSelectedTimezone] = useState<OptionType>(
		getInitialSelectedTimezone(initialConfig?.timezone)
	);

	const [selectedMonthDays, setSelectedMonthDays] = useState<OptionType[]>(
		initialConfig?.mdays
			? MONTHS_DAY_OPTIONS.filter((m) => initialConfig?.mdays?.includes(m?.value as number))
			: [MONTHS_DAY_OPTIONS[0]]
	);
	/** ev select new toggle   */
	const { getToggle } = useConfiguration();
	const isSchedulingV2Enabled = useMemo(
		() => getToggle(SiteScannerUiToggles.ENABLE_SCHEDULING_V2) || false,
		[]
	);

	const createUpdatedConfig = useMemo((): IScheduledJobConfiguration => {
		const scheduledJobConfig: IScheduledJobConfiguration = initialConfig
			? { ...initialConfig }
			: {};
		scheduledJobConfig.active = schedulingEnabled;
		scheduledJobConfig.frequency = selectedFrequency;
		const { minutes, hours } = parseTimeStr(selectedTime?.value as string);
		scheduledJobConfig.hours = [hours];
		scheduledJobConfig.minutes = [minutes];
		scheduledJobConfig.timezone = selectedTimezone?.value as string;

		if (!isSchedulingV2Enabled) {
			scheduledJobConfig.jobType = includeCrawl ? JobTypeDto.CRAWL_AND_SCAN : JobTypeDto.SCAN;
			if (scheduledJobConfig.frequency === FrequencyDto.MONTHLY) {
				scheduledJobConfig.wdays = [];
				scheduledJobConfig.mdays = selectedMonthDays.map((o) => o?.value as number);
			} else {
				scheduledJobConfig.wdays = [selectedWeekDay?.value as number];
				scheduledJobConfig.mdays = [];
			}
		}
		return scheduledJobConfig;
	}, [
		initialConfig,
		schedulingEnabled,
		selectedFrequency,
		selectedTime?.value,
		selectedTimezone?.value,
		isSchedulingV2Enabled,
		includeCrawl,
		selectedMonthDays,
		selectedWeekDay?.value
	]);

	useEffect(() => {
		if (initialConfig || schedulingEnabled) {
			const updatedConfig = createUpdatedConfig;
			setProperty((property: IProperty) => {
				property.scheduledJobConfiguration = updatedConfig;
				return property;
			});
		}
	}, [createUpdatedConfig, schedulingEnabled, initialConfig]);

	/** rendering functions  */
	const renderHeader = (): JSX.Element => {
		const helpTooltipContent: JSX.Element = (
			<>Set a start time and interval of when and how often scans will automatically be executed.</>
		);
		return (
			<div className="section-header">
				<div className="title">
					<EvTitle
						titleText="Schedule Scans"
						MoreInfoContent={helpTooltipContent}
						id={SCHEDULING_SETTINGS_TITLE}
						descriptionId={SCHEDULING_SETTINGS_DESCRIPTION}
					/>
					<div className="actions">
						<EvSwitch
							ariaLabel={`${schedulingEnabled ? 'Disable' : 'Enable'} crawl scheduling`}
							checked={schedulingEnabled}
							onChange={() => {
								setSchedulingEnabled((prev) => !prev);
							}}
						/>
					</div>
				</div>
			</div>
		);
	};

	const renderIncludeCrawlCheckbox = (): JSX.Element => {
		if (property?.urlSetConfiguration?.source === URL_SET_SOURCES.UPLOAD) {
			// no need to show toggle in list mode
			return null;
		}
		return (
			<EvCheckbox
				isChecked={includeCrawl}
				label="Include crawl"
				ariaLabel="Include crawl"
				onClick={() => {
					setIncludeCrawl((prev) => !prev);
				}}
				isDisabled={!schedulingEnabled}
			/>
		);
	};

	const renderFrequencySelection = (): JSX.Element => {
		const radioGroupOptions = [
			{
				label: 'Week',
				isChecked: selectedFrequency === FrequencyDto.WEEKLY,
				onClick: () => setSelectedFrequency(FrequencyDto.WEEKLY)
			},
			{
				label: 'Month',
				isChecked: selectedFrequency === FrequencyDto.MONTHLY,
				onClick: () => setSelectedFrequency(FrequencyDto.MONTHLY)
			}
		];
		return (
			<div className="frequency-selection">
				<EvRadioGroup
					title="Every"
					className="scheduling-frequency-radio-group"
					name="frequency-selection-radio"
					options={radioGroupOptions}
					isDisabled={!schedulingEnabled}
					ariaLabel="Scheduling Frequency Selection"
				/>
			</div>
		);
	};

	const renderFirstDropdown = (): JSX.Element => {
		if (selectedFrequency === FrequencyDto.MONTHLY) {
			return (
				<EvSelectNew
					isMultiple={true}
					isSearchable={false}
					className="day-in-month-select"
					placeholder="Day in month"
					selectedValue={selectedMonthDays}
					options={MONTHS_DAY_OPTIONS}
					onSelectChange={(selectedItem) => {
						setSelectedMonthDays(selectedItem);
					}}
					isDisabled={!schedulingEnabled}
					label=""
					id="day-in-month-select"
				/>
			);
		}

		return (
			<EvSelectNew
				isMultiple={false}
				isSearchable={false}
				placeholder="Week day"
				className="day-in-week-select"
				selectedValue={[selectedWeekDay]}
				options={WEEKDAY_OPTIONS}
				onSelectChange={(selectedItem) => {
					if (selectedItem?.[0]) {
						setSelectedWeekDay(selectedItem[0]);
					}
				}}
				isDisabled={!schedulingEnabled}
				label=""
				id="day-in-week-select"
			/>
		);
	};

	const renderDateSelection = (): JSX.Element => {
		return (
			<div className="date-selection">
				<p className="text">On</p>
				{renderFirstDropdown()}
				<EvSelectNew
					isMultiple={false}
					isSearchable={false}
					placeholder="Time"
					className="time-select"
					selectedValue={[selectedTime]}
					options={timeOptions}
					onSelectChange={(selectedItem) => {
						if (selectedItem?.[0]) {
							setSelectedTime(selectedItem[0]);
						}
					}}
					isDisabled={!schedulingEnabled}
					label=""
					id="time-select"
				/>
				<EvSelectNew
					isMultiple={false}
					isSearchable={true}
					placeholder="Timezone"
					className="timezone-select"
					selectedValue={[selectedTimezone]}
					options={timezoneOptions}
					onSelectChange={(selectedItem) => {
						if (selectedItem?.[0]) {
							setSelectedTimezone(selectedItem[0]);
						}
					}}
					isDisabled={!schedulingEnabled}
					label=""
					id="timezone-select"
				/>
			</div>
		);
	};

	const renderMonthWarning = (): JSX.Element => {
		if (selectedFrequency !== FrequencyDto.MONTHLY) {
			return null;
		}
		return (
			<div className="month-selection-warning">
				<EvIcon icon={BlueInfoIcon} />
				{monthlySelectionWarning}
			</div>
		);
	};

	const renderContent = (): JSX.Element => {
		return (
			<div className="scheduling-content">
				{renderIncludeCrawlCheckbox()}
				{renderFrequencySelection()}
				{renderDateSelection()}
				{renderMonthWarning()}
			</div>
		);
	};

	const error = useMemo(() => {
		return propertyError && <div className="tab-error">{propertyError.message}</div>;
	}, [propertyError]);

	const onConfigChange = (config: IScheduledJobConfiguration): void => {
		setProperty((property: IProperty) => {
			const scheduledJobConfiguration = config.active || initialConfig ? config : undefined;
			property.scheduledJobConfiguration = scheduledJobConfiguration;
			return property;
		});
	};

	return !isSchedulingV2Enabled ? (
		<EvSection
			className="scheduling-settings-section"
			ariaLabelledby={SCHEDULING_SETTINGS_TITLE}
			ariaDescribedby={SCHEDULING_SETTINGS_DESCRIPTION}
		>
			<div className="scheduling-settings-content">
				{renderHeader()}
				<div className="section-body">
					{renderContent()}
					{error}
				</div>
			</div>
		</EvSection>
	) : (
		<SchedulingSection
			initialConfig={initialConfig}
			onConfigChange={onConfigChange}
			error={propertyError}
		/>
	);
};
